import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useWeb3Context } from '../../contexts/web3Context'
import { getAllianceTaskStatus, type AllianceTaskStatus } from '../../common/allianceApi'
import { isOnBattlePath } from '../../common/battleRoutes'
import { AllianceTaskVoteModal } from './AllianceTaskVoteModal'
import { AllianceTaskResultModal } from './AllianceTaskResultModal'

/** Re-check season state often so a season rollover (and its new vote prompt) is caught promptly. */
const POLL_MS = 60 * 1000

/**
 * Global gating watcher (mounted once in App). On load — and every minute —
 * it checks the player's alliance task season:
 *   - Leader with a closed season awaiting a decision → forced result modal (no dismiss).
 *   - Member who hasn't voted this season → vote modal. Dismissable, but it re-appears at the
 *     start of EVERY new season (the dismissal resets when the season key changes).
 * No-ops for players not in an alliance.
 */
export const AllianceTaskWatcher: React.FC = () => {
  const { address } = useWeb3Context()
  const { pathname } = useLocation()
  const [status, setStatus] = useState<AllianceTaskStatus | null>(null)
  const [voteDismissed, setVoteDismissed] = useState(false)

  // Reset the vote dismissal at the start of each new season, so the vote modal always
  // reappears when a season begins (not just once per session).
  const lastSeasonKey = useRef<string | null>(null)

  const refresh = useCallback(async () => {
    if (!address) {
      setStatus(null)
      return
    }
    try {
      const s = await getAllianceTaskStatus(address)
      const key = s.taskPeriod?.seasonKey ?? null
      if (key !== lastSeasonKey.current) {
        lastSeasonKey.current = key
        setVoteDismissed(false) // new season → re-prompt to vote
      }
      setStatus(s)
    } catch {
      // 404 (not in an alliance) or transient error → show nothing.
      setStatus(null)
    }
  }, [address])

  // Reset per-wallet state when the wallet changes.
  const lastAddress = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (lastAddress.current !== address) {
      lastAddress.current = address
      lastSeasonKey.current = null
      setVoteDismissed(false)
    }
  }, [address])

  useEffect(() => {
    void refresh()
    if (!address) return
    const id = window.setInterval(() => void refresh(), POLL_MS)
    return () => window.clearInterval(id)
  }, [address, refresh])

  if (!address || !status) return null

  // Never interrupt an active battle (MvM / PvP / PvE / tournament). The status persists, so once the
  // player leaves the battle screen this component re-renders (useLocation) and the modal appears.
  if (isOnBattlePath(pathname)) return null

  // Leader decision takes priority and cannot be dismissed.
  if (status.isLeader && status.needsLeaderFinalize && status.finalizePeriod) {
    return (
      <AllianceTaskResultModal
        open
        address={address}
        allianceId={status.allianceId}
        period={status.finalizePeriod}
        onFinalized={() => void refresh()}
      />
    )
  }

  if (!status.isLeader && status.needsVote && status.taskPeriod && !voteDismissed) {
    return (
      <AllianceTaskVoteModal
        open
        address={address}
        period={status.taskPeriod}
        onClose={() => setVoteDismissed(true)}
        onVoted={() => void refresh()}
      />
    )
  }

  return null
}

export default AllianceTaskWatcher
